/**
 * ConnectionErrorScreen — shown when the WS connection to the remote instance
 * fails after all retry attempts. Replaces the broken "local UI" that renders
 * with empty data.
 *
 * Features:
 * - Clear error message with connection details
 * - "Report Issue" button → captures to Sentry with full context
 * - "Back to Launcher" button → returns to instance picker
 * - "Retry" button → attempts fresh connection
 */

import { useRetryStore } from '@khal-os/desktop-shell';
import { useEffect, useState } from 'react';
import { getAdapter } from '../lib/adapters';
import { resolveDefaultKernelForPwaOrigin } from '../lib/default-kernel';
import { Sentry } from '../sentry';

interface ConnectionErrorScreenProps {
	/** The server URL the client tried to connect to */
	serverUrl?: string;
	/** Callback to navigate back to the launcher */
	onBackToLauncher: () => void;
	/** Callback to retry the connection */
	onRetry: () => void;
}

export function ConnectionErrorScreen({ serverUrl, onBackToLauncher, onRetry }: ConnectionErrorScreenProps) {
	const retryState = useRetryStore();
	const [reported, setReported] = useState(false);
	const [retrying, setRetrying] = useState(false);

	// If connection succeeds while on this screen, let the parent handle it
	useEffect(() => {
		if (retryState.status === 'connected') {
			setRetrying(false);
		}
	}, [retryState.status]);

	function handleReport() {
		const eventId = Sentry.captureException(
			new Error(`Connection failed to ${serverUrl || 'unknown instance'}`),
			{
				tags: {
					component: 'ConnectionErrorScreen',
					server_url: serverUrl || 'unknown',
					retry_count: String(retryState.retryCount),
				},
				extra: {
					retryState: {
						status: retryState.status,
						retryCount: retryState.retryCount,
						maxRetries: retryState.maxRetries,
					},
					serverUrl,
					userAgent: navigator.userAgent,
					timestamp: new Date().toISOString(),
				},
			}
		);

		setReported(true);
		console.log('[ConnectionErrorScreen] Issue reported to Sentry, eventId:', eventId);
	}

	function handleRetry() {
		setRetrying(true);
		setReported(false);
		onRetry();
	}

	async function handleSignOut() {
		// Purge stored enterprise config on any device identity (PWA or Tauri)
		// and bounce to the platform sign-in. The user has hit a wall and
		// the session they do have isn't letting them through — resetting
		// is the only unconditional escape.
		const adapter = getAdapter();
		try {
			await adapter.clearEnterpriseConfig?.();
		} catch (err) {
			console.error('[ConnectionErrorScreen] clearEnterpriseConfig failed', err);
		}
		const platformUrl = import.meta.env.VITE_PLATFORM_API_URL ?? 'https://platform.khal.ai';
		// Prefer the kernel we were just trying to reach, then a build-time
		// override; otherwise drop the user on the URL-prompt screen so they
		// can name a different workspace explicitly. No customer-specific
		// hardcoded fallback.
		const candidate = serverUrl ?? resolveDefaultKernelForPwaOrigin({
			pwaOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
			buildDefaultKernel: import.meta.env.VITE_DEFAULT_KERNEL,
		});
		const pwaOrigin = window.location.origin;
		if (!candidate) {
			window.location.replace('/auth/start');
			return;
		}
		window.location.replace(
			`${platformUrl}/auth/kernel-sign-in?kernel=${encodeURIComponent(candidate)}&pwaOrigin=${encodeURIComponent(pwaOrigin)}`,
		);
	}

	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				background: '#0a0a0a',
				color: '#e0e0e0',
				fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				zIndex: 99999,
				padding: '2rem',
			}}
		>
			{/* Error icon */}
			<div
				style={{
					width: 64,
					height: 64,
					borderRadius: '50%',
					background: 'rgba(239, 68, 68, 0.15)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					marginBottom: '1.5rem',
				}}
			>
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<circle cx="12" cy="12" r="10" />
					<line x1="12" y1="8" x2="12" y2="12" />
					<line x1="12" y1="16" x2="12.01" y2="16" />
				</svg>
			</div>

			<h1
				style={{
					fontSize: '1.5rem',
					fontWeight: 600,
					margin: '0 0 0.5rem 0',
					color: '#ffffff',
				}}
			>
				Connection Failed
			</h1>

			<p
				style={{
					fontSize: '0.9rem',
					color: '#888',
					margin: '0 0 0.25rem 0',
					textAlign: 'center',
					maxWidth: '400px',
				}}
			>
				Could not connect to {serverUrl || 'the remote instance'} after {retryState.maxRetries} attempts.
			</p>

			<p
				style={{
					fontSize: '0.8rem',
					color: '#555',
					margin: '0 0 2rem 0',
					textAlign: 'center',
				}}
			>
				The server may be down, or your session may have expired.
			</p>

			{/* Buttons */}
			<div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
				<button
					type="button"
					onClick={handleRetry}
					disabled={retrying}
					style={{
						padding: '0.6rem 1.5rem',
						borderRadius: '8px',
						border: '1px solid #333',
						background: '#1a1a1a',
						color: '#fff',
						fontSize: '0.85rem',
						fontWeight: 500,
						cursor: retrying ? 'not-allowed' : 'pointer',
						opacity: retrying ? 0.5 : 1,
						transition: 'all 0.15s',
					}}
				>
					{retrying ? 'Retrying...' : 'Retry Connection'}
				</button>

				<button
					type="button"
					onClick={handleReport}
					disabled={reported}
					style={{
						padding: '0.6rem 1.5rem',
						borderRadius: '8px',
						border: reported ? '1px solid #22c55e' : '1px solid #ef4444',
						background: reported ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
						color: reported ? '#22c55e' : '#ef4444',
						fontSize: '0.85rem',
						fontWeight: 500,
						cursor: reported ? 'default' : 'pointer',
						transition: 'all 0.15s',
					}}
				>
					{reported ? 'Issue Reported' : 'Report Issue'}
				</button>

				<button
					type="button"
					onClick={onBackToLauncher}
					style={{
						padding: '0.6rem 1.5rem',
						borderRadius: '8px',
						border: 'none',
						background: '#2563eb',
						color: '#fff',
						fontSize: '0.85rem',
						fontWeight: 500,
						cursor: 'pointer',
						transition: 'all 0.15s',
					}}
				>
					Back to Launcher
				</button>
			</div>

			{reported && (
				<p
					style={{
						fontSize: '0.75rem',
						color: '#555',
						marginTop: '1rem',
					}}
				>
					A diagnostic report has been sent. Our team will investigate.
				</p>
			)}

			<button
				type="button"
				onClick={handleSignOut}
				style={{
					marginTop: '2rem',
					padding: '0.4rem 0.9rem',
					borderRadius: '6px',
					border: 'none',
					background: 'transparent',
					color: '#888',
					fontSize: '0.75rem',
					cursor: 'pointer',
					textDecoration: 'underline',
				}}
			>
				Sign out and try a different workspace
			</button>
		</div>
	);
}
